import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import AppImage from '../../components/AppImage';
import { Phone, Luggage, Users } from 'lucide-react';

const TempoTravellerMain = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const vehicleTypes = [
  { name: 'Tempo Traveller', pricePerKm: 28, seats: 12, bags: 8, image: "https://img.rocket.new/generatedImages/rocket_gen_img_16f41fd6f-1768440186961.png", alt: 'White tempo traveller 12 seater minibus for group travel' },
  { name: 'Tempo Traveller', pricePerKm: 30, seats: 17, bags: 15, image: "https://img.rocket.new/generatedImages/rocket_gen_img_114e4d671-1768440186864.png", alt: 'White tempo traveller 17 seater for large group travel' },
  { name: 'Force Urbania', pricePerKm: 38, seats: 16, bags: 16, image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d461a4d4-1768082329835.png', alt: 'Force Urbania luxury van 16 seater with premium interiors' },
  { name: 'Tempo Traveller', pricePerKm: 38, seats: 25, bags: 15, image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b6efb5fd-1765716730341.png', alt: 'Extra large tempo traveller 25 seater for large group travel' }];


  const cities = [
  { name: 'Amritsar', image: 'https://images.unsplash.com/photo-1689141083335-fc7aa682b166', alt: 'Golden Temple in Amritsar illuminated at night with reflection in holy pool' },
  { name: 'Chandigarh', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dab7fc8f-1768337149171.png", alt: 'Rock Garden sculpture in Chandigarh - famous tourist attraction' },
  { name: 'Delhi', image: "https://images.unsplash.com/photo-1703083664356-a15a04d42e4c", alt: 'India Gate monument in Delhi at sunset with reflection' },
  { name: 'Mohali', image: "https://img.rocket.new/generatedImages/rocket_gen_img_15117a63c-1768145557796.png", alt: 'Modern cityscape of Mohali with cricket stadium' },
  { name: 'Patiala', image: "https://img.rocket.new/generatedImages/rocket_gen_img_14f5d8b44-1768145561104.png", alt: 'Qila Mubarak fort in Patiala with traditional Punjabi architecture' }];


  const handleViewRoutes = (cityName) => {
    navigate(`/tempo-traveller/${cityName?.toLowerCase()}`);
  };

  return (
    <>
      <SEO
        title="Tempo Traveller From - Book Tempo Traveller on Rent | Advait Tour and Travels"
        description="Book tempo traveller on rent from major cities. 12, 17, 16, and 25 seater options available. Comfortable group travel with professional drivers."
        keywords="tempo traveller on rent, tempo traveller from amritsar, tempo traveller from chandigarh, tempo traveller from delhi, tempo traveller booking"
        canonicalUrl={`${window.location?.origin}/tempo-traveller`}
        ogTitle="Tempo Traveller From - Book Tempo Traveller on Rent | Advait Tour and Travels"
        ogDescription="Book tempo traveller on rent from major cities. 12, 17, 16, and 25 seater options available. Comfortable group travel with professional drivers."
        ogUrl={`${window.location?.origin}/tempo-traveller`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Advait Tour and Travels",
          "description": "Book tempo traveller on rent from major cities"
        }} />

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="py-20">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-16 mb-12">
            <div className="container mx-auto px-4">
              <h1 className="text-4xl md:text-5xl font-bold text-white text-center">
                Tempo Traveller From
              </h1>
            </div>
          </div>

          {/* Vehicle Type Cards */}
          <div className="container mx-auto px-4 mb-16">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-8">
              Our Fleet
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {vehicleTypes?.map((vehicle, index) =>
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">

                  <div className="relative h-48">
                    <AppImage
                    src={vehicle?.image}
                    alt={vehicle?.alt}
                    className="w-full h-full object-cover" />

                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                      {vehicle?.name}
                    </h3>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-center gap-2 text-gray-700">
                        <span className="font-semibold">₹{vehicle?.pricePerKm}/km</span>
                      </div>
                      <div className="flex items-center justify-center gap-4 text-gray-600">
                        <div className="flex items-center gap-1">
                          <Users size={18} />
                          <span>Seaters: {vehicle?.seats}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-center gap-4 text-gray-600">
                        <div className="flex items-center gap-1">
                          <Luggage size={18} />
                          <span>Bag: {vehicle?.bags}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* City Cards */}
          <div className="container mx-auto px-4 mb-16">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-8">
              Select Your City
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {cities?.map((city, index) =>
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">

                  <div className="relative h-48">
                    <AppImage
                    src={city?.image}
                    alt={city?.alt}
                    className="w-full h-full object-cover" />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
                      {city?.name}
                    </h3>
                    <button
                    onClick={() => handleViewRoutes(city?.name)}
                    className="w-full py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-lg transition-colors duration-300">

                      VIEW ROUTES
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-12">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                We're here for you 24/7!
              </h2>
              <p className="text-xl text-white mb-6">
                Give us a call anytime
              </p>
              <a
                href="tel:+917998666660"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-2xl rounded-full hover:bg-gray-100 transition-colors duration-300">

                <Phone className="w-6 h-6" />
                +91-79986-66660
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>);

};

export default TempoTravellerMain;